import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  PinInput,
  PinInputField,
  Text,
  VStack
} from "@chakra-ui/react";
import ReCAPTCHA from "react-google-recaptcha";
import {useEffect, useState} from "react";
import {useAPI} from "../hooks/useAPI";
import {Field, Formik} from "formik";
import * as yup from "yup";
import {useNavigate} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {peekPendingBooking} from "../services/pendingBooking";

const AuthPage = () => {
  const api = useAPI();
  const navigate = useNavigate();

  const [step, setStep] = useState("credentials");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    // only bounce an already-signed-in visitor away; the pin/name steps finish on their own
    if (api.loggedIn && step === "credentials") {
      const pending = peekPendingBooking();
      navigate(pending ? pending.returnTo : "/profile");
    }
  }, [navigate, api.loggedIn, step])

  const doAuth = (values: {email: string, captcha: string}) => {
    setLoading(true);
    api.auth(values.email, values.captcha)
      .catch(err => {
        console.error(err)
        throw err;
      })
      .then(value => {
        if (value !== null) {
          setEmail(values.email)
          setStep("pin")
        }
      })
      .finally(() => setLoading(false))
  }

  /** Where to continue after signing in: back to a pending booking, or to the profile. */
  const finish = () => {
    const pending = peekPendingBooking();
    window.location.replace(pending ? pending.returnTo : "/profile");
  }

  const onPinChange = async (value: string) => {
    if (value.length === 6) {
      try {
        setLoading(true);
        if (await api.getToken(email, value)) {
          // a brand new profile has no name yet — ask for it before continuing
          await api.checkAuthorization();
          if (!api.loggedInUser?.has_name) {
            setStep("name");
            return;
          }
          finish();
        }
      } catch (e: any) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  }

  const saveName = ({name}: { name: string }) => {
    setLoading(true);
    api.patchProfile({name})
      .then((response) => {
        if (response === null) {
          return;
        }
        api.loggedInUser = response;
        finish();
      })
      .finally(() => setLoading(false));
  }

  const HEADINGS: Record<string, { title: string, hint: string }> = {
    credentials: {
      title: "Sign in",
      hint: "Fill the following form to start creating your wishlists immediately. If you don't have an account yet, we'll create it for you.",
    },
    pin: {
      title: "Check your mailbox",
      hint: `We've sent a 6-character code to ${email}. The code expires shortly, so please enter it soon.`,
    },
    name: {
      title: "How should we call you?",
      hint: "Your name is shown to the people whose wishlists you book gifts from.",
    },
  };

  return <Box bg="white" p={3} textAlign="center">
    <Heading mt={5} size={{base: "md", sm: "lg"}}>{HEADINGS[step].title}</Heading>
    <Text mx="auto" mt={3} size={{base: "xs", sm: "sm"}} color="gray.500" maxW="470px" w="100%">
      {HEADINGS[step].hint}
    </Text>

    <Box mt="3rem" mx="auto" w={{base: '100%', sm: '75%', md: '60%'}}>
      {step === "credentials" && (
        <Formik
          initialValues={{ email: '', captcha: ''}}
          onSubmit={doAuth}
          validationSchema={
            yup.object().shape({
              email: yup.string().required().email(),
              captcha: yup.string().required()
            })
          }
        >
          {({ handleSubmit, errors, touched, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <FormControl textAlign="left" isInvalid={!!errors.email && touched.email}>
                <FormLabel>Email</FormLabel>
                <Field
                  id="email"
                  name="email"
                  as={Input}
                  type="email"
                  placeholder="your-email@example.com"
                  readOnly={loading}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
                <FormHelperText>We'll never share your email.</FormHelperText>
              </FormControl>

              <FormControl mt={5} isInvalid={!!errors.captcha && touched.captcha}>
                <ReCAPTCHA
                  sitekey="6LcabLIkAAAAABfLA66ymnaOeahDa2p-fgQvf5tK"
                  onChange={(value: string | null) => {setFieldValue("captcha", value)}}/>
                <FormErrorMessage>{errors.captcha}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                my={5}
                colorScheme="brand"
                w="40%"
                disabled={loading}
              >
                {loading ? 'Please wait...' : 'Sign in'}
              </Button>
            </form>
          )}
        </Formik>
      )}
      {step === "pin" && (
        <>
          <VStack justifyContent="center" mb={5}>
            <HStack>
              <PinInput otp size="lg" onChange={onPinChange} isDisabled={loading}>
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
          </VStack>
        </>
      )}
      {step === "name" && (
        <Formik
          initialValues={{name: ''}}
          onSubmit={saveName}
          validationSchema={yup.object().shape({
            name: yup.string().required().min(2).max(30)
          })}
        >
          {({handleSubmit, errors, touched}) => (
            <form onSubmit={handleSubmit}>
              <FormControl textAlign="left" isInvalid={!!errors.name && touched.name}>
                <FormLabel>Your name</FormLabel>
                <Field id="name" name="name" as={Input} type="text" placeholder="Alex" readOnly={loading}/>
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <Button type="submit" my={5} colorScheme="brand" w="40%" isDisabled={loading}>
                {loading ? 'Please wait...' : 'Continue'}
              </Button>
            </form>
          )}
        </Formik>
      )}
    </Box>
  </Box>
}

export default observer(AuthPage);
